import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Sparkles, Loader2, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import AiChatPanel from "./AiChatPanel";
import CampaignDraftReview from "./CampaignDraftReview";
import {
  aiCreateSession,
  aiStreamMessage,
  aiGetSession,
  aiFinalize,
  aiExport,
} from "@/lib/ai";

/**
 * Full-page overlay for the AI Campaign Writer.
 *
 * Two-pane UX:
 *   Left  — chat interview
 *   Right — draft preview (empty until finalized)
 */
const CampaignWriterModal = ({ open, onClose, sessionId: initialSessionId = null }) => {
  const [sessionId, setSessionId] = useState(initialSessionId);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [readyToGenerate, setReadyToGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);       // latest generation.data
  const [generationId, setGenerationId] = useState(null);
  const abortRef = useRef(null);
  const navigate = useNavigate();

  // Initialize session on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const boot = async () => {
      try {
        if (sessionId) {
          const { session, messages, latest_generation } = await aiGetSession(sessionId);
          if (cancelled) return;
          setMessages(messages);
          if (latest_generation) { setDraft(latest_generation.data); setGenerationId(latest_generation.id); }
        } else {
          const s = await aiCreateSession({ feature: "writer" });
          if (cancelled) return;
          setSessionId(s.id);
          const { messages } = await aiGetSession(s.id);
          setMessages(messages);
        }
      } catch (e) { toast.error(e.message || "Could not start AI session"); }
    };
    boot();
    return () => { cancelled = true; abortRef.current?.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const appendMessage = (m) => setMessages((prev) => [...prev, m]);

  const sendTurn = async (message) => {
    if (!sessionId) return;
    const userMsg = { id: `local_${Date.now()}`, role: "user", content: message };
    appendMessage(userMsg);
    setStreaming(true);
    setStreamingText("");
    setReadyToGenerate(false);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      let assembled = "";
      await aiStreamMessage(sessionId, message, (ev) => {
        if (ev.delta) {
          assembled += ev.delta;
          setStreamingText(assembled);
        } else if (ev.done) {
          // Strip the sentinel from the visible content
          const clean = assembled.replace(/READY_TO_GENERATE/g, "").trim();
          setMessages((prev) => [...prev, { id: `local_a_${Date.now()}`, role: "assistant", content: clean }]);
          setStreamingText("");
          if (ev.ready_to_generate) setReadyToGenerate(true);
        } else if (ev.error) {
          toast.error(ev.error);
        }
      }, controller.signal);
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Stream failed");
    } finally {
      setStreaming(false);
    }
  };

  const onFinalize = async () => {
    if (!sessionId || generating) return;
    setGenerating(true);
    try {
      const gen = await aiFinalize(sessionId);
      setDraft(gen.data);
      setGenerationId(gen.id);
      toast.success("Your campaign draft is ready");
    } catch (e) {
      toast.error(e.message || "Could not generate draft");
    } finally {
      setGenerating(false);
    }
  };

  const onExport = async () => {
    if (!sessionId || !generationId || !draft) return;
    try {
      await aiExport(sessionId, generationId);
      const payload = { ...draft, __aiSessionId: sessionId, __aiGenerationId: generationId };
      sessionStorage.setItem("dwinov:aiDraft", JSON.stringify(payload));
      toast.success("Draft loaded into campaign builder");
      onClose?.();
      navigate("/start?ai=1");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const suggestions = useMemo(() => [], []);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-navy-900/60 backdrop-blur-sm" data-testid="ai-writer-modal">
      <div className="absolute inset-2 sm:inset-4 md:inset-8 rounded-[24px] bg-cream-100 overflow-hidden shadow-warm-lg border border-warm-200 flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-3 border-b border-warm-200 bg-white/60 backdrop-blur">
          <div className="flex items-center gap-3 min-w-0">
            <div className="grid place-items-center h-9 w-9 rounded-xl bg-navy-800 flex-shrink-0">
              <Sparkles className="h-4 w-4 text-saffron-400" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-bold text-navy-800 tracking-tight">AI Campaign Writer</div>
              <div className="text-[11px] text-navy-500 truncate">Powered by Claude Sonnet 4.6 · Answers stream in real time</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="ai-writer-close"
            className="grid place-items-center h-9 w-9 rounded-xl text-navy-600 hover:bg-warm-100 hover:text-navy-800"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-5">
          {/* Left: chat */}
          <div className="lg:col-span-3 flex flex-col min-h-0 border-r border-warm-200">
            <AiChatPanel
              messages={messages}
              streaming={streaming}
              streamingText={streamingText.replace(/READY_TO_GENERATE/g, "").trim()}
              onSend={sendTurn}
              readyToGenerate={readyToGenerate && !draft}
              onFinalize={onFinalize}
              placeholder={draft ? "Ask for tweaks or refinements…" : "Type your answer…"}
              suggestions={suggestions}
            />
          </div>

          {/* Right: draft preview */}
          <div className="lg:col-span-2 flex flex-col min-h-0 bg-white">
            {generating && (
              <div className="flex-1 grid place-items-center text-center px-6">
                <div>
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-saffron-600" />
                  <p className="mt-4 font-display font-bold text-navy-800">Drafting your campaign…</p>
                  <p className="text-sm text-navy-500 mt-1">Claude is writing your story, rewards, timeline, and FAQs. This usually takes 15–30 seconds.</p>
                </div>
              </div>
            )}

            {!generating && !draft && (
              <div className="flex-1 grid place-items-center text-center px-6" data-testid="ai-writer-empty-draft">
                <div>
                  <div className="mx-auto grid place-items-center h-12 w-12 rounded-2xl bg-warm-100 mb-4">
                    <FileText className="h-6 w-6 text-navy-400" />
                  </div>
                  <p className="font-display font-bold text-navy-800">Your draft will appear here</p>
                  <p className="text-sm text-navy-500 mt-2 max-w-xs">Answer a handful of questions on the left. When you're done, tap <span className="font-semibold text-saffron-700">Generate my full campaign draft</span>.</p>
                </div>
              </div>
            )}

            {!generating && draft && (
              <>
                <div className="flex items-center justify-between px-5 py-3 border-b border-warm-200 bg-cream-100/70">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-saffron-600" />
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-saffron-700">Draft v1</span>
                  </div>
                  <button
                    onClick={onExport}
                    data-testid="ai-writer-export-btn"
                    className="btn-saffron inline-flex items-center gap-1.5 rounded-2xl h-9 px-4 text-sm font-semibold"
                  >
                    <Download className="h-3.5 w-3.5" /> Export to campaign
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <CampaignDraftReview
                    sessionId={sessionId}
                    draft={draft}
                    onDraftChange={setDraft}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignWriterModal;
